/**
 * Authentication Page - Clean Design Inspired by Fields Manager
 * 
 * Simple authentication page with Google sign-in and email/password fields (coming soon).
 */
import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  Link,
  InputAdornment
} from '@mui/material';
import { Email, Lock } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import LoadingScreen from '../components/LoadingScreen';

const Auth = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to home if already authenticated
  useEffect(() => {
    if (user && !loading) {
      const from = location.state?.from?.pathname || '/';
      console.log('✅ User authenticated, redirecting to:', from);
      navigate(from, { replace: true });
    }
  }, [user, loading, navigate, location]);

  // Show loading screen while checking auth
  if (loading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // Don't render if user is authenticated (will redirect)
  if (user) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 2,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* BCE Background Blobs */}
      <div className="eclipse" style={{ width: '456px', height: '456px', top: '10%', left: '-100px' }} />
      <div className="planet" style={{ width: '424px', height: '424px', bottom: '10%', right: '-100px' }} />
      
      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            padding: 5,
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff'
          }}
        >
          {/* BCE Logo */}
          <Box
            sx={{
              width: 64,
              height: 64,
              margin: '0 auto 24px',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(89, 92, 234, 0.3)'
            }}
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              marginBottom: 1,
              color: '#1e1e1e'
            }}
          >
            Welcome to BCE
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: 'DM Sans, sans-serif',
              color: 'rgba(30, 30, 30, 0.7)',
              marginBottom: 4
            }}
          >
            Sign in to your SOW Generator account
          </Typography>

          {/* Google Sign-In Button */}
          <GoogleSignInButton />

          {/* Divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', marginY: 3 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            <Typography variant="body2" sx={{ px: 2, color: '#9e9e9e', fontSize: '13px' }}>
              Or continue with
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          </Box>

          {/* Email/Password Form - Coming Soon */}
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500, color: '#1e1e1e', fontFamily: 'DM Sans, sans-serif' }}>
              Email address
            </Typography>
            <TextField
              fullWidth
              placeholder="user@example.com"
              disabled
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: '#bdbdbd', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                marginBottom: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa',
                  borderRadius: 2,
                  fontSize: '14px'
                }
              }}
            />

            <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 500, color: '#1e1e1e', fontFamily: 'DM Sans, sans-serif' }}>
              Password
            </Typography>
            <TextField
              fullWidth
              type="password"
              placeholder="Enter your password"
              disabled
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: '#bdbdbd', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                marginBottom: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fafafa',
                  borderRadius: 2,
                  fontSize: '14px'
                }
              }}
            />

            <Button
              fullWidth
              variant="contained"
              disabled
              sx={{
                padding: '10px',
                textTransform: 'none',
                fontSize: '15px',
                fontWeight: 600,
                fontFamily: 'DM Sans, sans-serif',
                borderRadius: 2,
                marginBottom: 2,
                backgroundColor: '#595cea',
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                },
                '&:hover': {
                  backgroundColor: '#4a4dd6',
                }
              }}
            >
              Sign in
            </Button>

            <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: '#9e9e9e',
                  textDecoration: 'none',
                  fontSize: '13px',
                  pointerEvents: 'none',
                  '&:hover': {
                    textDecoration: 'none'
                  }
                }}
              >
                Forgot your password?
              </Link>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#757575', fontSize: '13px' }}>
                Don't have an account?{' '}
                <Link
                  href="#"
                  sx={{
                    color: '#9e9e9e',
                    textDecoration: 'none',
                    fontWeight: 500,
                    pointerEvents: 'none'
                  }}
                >
                  Sign up
                </Link>
              </Typography>
            </Box>
          </Box>

          {/* Footer */}
          <Box sx={{ marginTop: 4, paddingTop: 3, borderTop: '1px solid #f0f0f0' }}>
            <Typography variant="caption" sx={{ color: 'rgba(30, 30, 30, 0.5)', fontSize: '12px', fontFamily: 'DM Sans, sans-serif' }}>
              🔒 Secured by AWS Cognito | Powered by BizCloud Experts
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Auth;
